/**
 * Tenant voiceprint library — "Known voices".
 *
 * Owner/admin-facing view of every Speaker identity the org has recognised, each
 * annotated with how many meetings it has been recognised in (the visible
 * voiceprint moat). Expanding a row loads that identity's voiceprint sample
 * history; a bad sample can be revoked, or the whole identity deleted. This is the
 * tenant-facing counterpart to the super-admin per-company enrollment page
 * (`/companies/$id/speakers`) — same owner/admin API, tenant framing.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Container, Group, Stack, Text, Title } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { KnownVoiceCard } from "../../components/KnownVoiceCard"
import type { IKnownVoice } from "../../components/KnownVoiceCard/interfaces"
import { LineListSkeleton } from "../../components/PageSkeletons"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { requireCompanyManager } from "../../lib/route-guards"

interface ISpeakersSearch {
  voice?: string
}

function KnownVoicesPage(): React.JSX.Element {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const { voice } = Route.useSearch()
  const [expanded, setExpanded] = useState<Set<string>>(() => (voice ? new Set([voice]) : new Set()))

  useEffect(() => {
    if (!voice) return
    setExpanded((prev) => {
      if (prev.has(voice)) return prev
      const next = new Set(prev)
      next.add(voice)
      return next
    })
  }, [voice])

  const { data: speakers, isLoading } = useQuery({
    queryKey: queryKeys.companySpeakers(orgId),
    queryFn: async (): Promise<IKnownVoice[]> => {
      if (!company) return []
      const res = await company.speakers.get()
      if (res.error) throw new Error(extractErrorMessage(res.error))
      // Most-recognised identities first — the moat, front and centre.
      return [...(res.data?.data ?? [])].sort((a, b) => b.recognized_meeting_count - a.recognized_meeting_count)
    },
    enabled: !!company,
  })

  const { mutate: revoke } = useMutation({
    mutationFn: async ({ speakerId, embeddingId }: { speakerId: string; embeddingId: string }) => {
      if (!company) throw new Error(i18n._(msg`No active organization`))
      const res = await company.speakers({ speakerId }).enrollments({ embeddingId }).delete()
      if (res.error) throw new Error(extractErrorMessage(res.error))
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.speakerEnrollments(orgId, variables.speakerId) })
      notifications.show({
        title: i18n._(msg`Sample revoked`),
        message: i18n._(msg`The voice sample has been deleted.`),
        color: "success",
      })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: remove, variables: removingVars } = useMutation({
    mutationFn: async (speakerId: string) => {
      if (!company) throw new Error(i18n._(msg`No active organization`))
      const res = await company.speakers({ speakerId }).delete()
      if (res.error) throw new Error(extractErrorMessage(res.error))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companySpeakers(orgId) })
      notifications.show({
        title: i18n._(msg`Voice deleted`),
        message: i18n._(msg`The identity and its voice samples were removed.`),
        color: "danger",
      })
    },
    onError: (err) => notifyError(err),
  })

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>
          <Trans>Known voices</Trans>
        </Title>
      </Group>

      <Text c="dimmed" mb="md" size="sm">
        <Trans>
          Every voice this workspace has learned to recognise. The count shows how many meetings each person has been
          recognised in — the more they attend, the more reliably they're identified automatically. Expand a row to see
          the voiceprint samples behind a voice; revoke a bad sample, or delete a voice entirely.
        </Trans>
      </Text>

      {isLoading && <LineListSkeleton rows={4} />}

      {!isLoading && (speakers?.length ?? 0) === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          <Trans>No known voices yet — they're learned as you record meetings.</Trans>
        </Text>
      )}

      <Stack gap="xs">
        {speakers?.map((s) => (
          <KnownVoiceCard
            key={s.id}
            speaker={s}
            expanded={expanded.has(s.id)}
            onToggle={() => toggle(s.id)}
            onRevoke={(embeddingId) => revoke({ speakerId: s.id, embeddingId })}
            onDelete={() => remove(s.id)}
            deleting={removingVars === s.id}
            highlighted={voice === s.id}
          />
        ))}
      </Stack>
    </Container>
  )
}

export const Route = createFileRoute("/_auth/speakers")({
  beforeLoad: requireCompanyManager,
  validateSearch: (search: Record<string, unknown>): ISpeakersSearch => {
    const voice = typeof search.voice === "string" && search.voice.length > 0 ? search.voice : undefined
    return voice ? { voice } : {}
  },
  component: KnownVoicesPage,
})
